import { createElement } from 'react';
import {
  ApiBlueprint,
  PageBlueprint,
  createFrontendPlugin,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import {
  isApplicationGroupV1Entity,
  isStackV1Entity,
} from '@internal/plugin-stack-common';
import AssessmentIcon from '@material-ui/icons/Assessment';
import { PolicyClient, policyApiRef } from './api';
import { StudioClient, studioApiRef } from './studio/api';

const policyApi = ApiBlueprint.make({
  name: 'policy',
  params: defineApi =>
    defineApi({
      api: policyApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) =>
        new PolicyClient(discoveryApi, fetchApi),
    }),
});

const studioApi = ApiBlueprint.make({
  name: 'studio',
  params: defineApi =>
    defineApi({
      api: studioApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) =>
        new StudioClient(discoveryApi, fetchApi),
    }),
});

const applicationGroupPoliciesTab = EntityContentBlueprint.make({
  name: 'application-group-policies',
  params: {
    path: '/policies',
    title: 'Policies',
    filter: isApplicationGroupV1Entity,
    loader: () =>
      import('./components/ApplicationGroupPoliciesPage').then(m =>
        createElement(m.ApplicationGroupPoliciesPage),
      ),
  },
});

const applicationGroupQuotasTab = EntityContentBlueprint.make({
  name: 'application-group-quotas',
  params: {
    path: '/quotas',
    title: 'Quotas',
    filter: isApplicationGroupV1Entity,
    loader: () =>
      import('./components/quotas/ApplicationGroupQuotasPage').then(m =>
        createElement(m.ApplicationGroupQuotasPage),
      ),
  },
});

const stackPolicyEvaluationsTab = EntityContentBlueprint.make({
  name: 'stack-policy-evaluations',
  params: {
    path: '/policy-evaluations',
    title: 'Policy evaluations',
    filter: isStackV1Entity,
    loader: () =>
      import('./components/StackPolicyEvaluationsPage').then(m =>
        createElement(m.StackPolicyEvaluationsPage),
      ),
  },
});

const policyCatalogDetailsTab = EntityContentBlueprint.make({
  name: 'policy-catalog-details',
  params: {
    path: '/policy',
    title: 'Policy',
    filter: entity =>
      !!entity &&
      entity.apiVersion === 'aeven.io/v1alpha1' &&
      ['PolicyRevision', 'PolicyBinding', 'PolicyQuota'].includes(entity.kind),
    loader: () =>
      import('./components/PolicyCatalogEntityPage').then(m =>
        createElement(m.PolicyCatalogEntityPage),
      ),
  },
});

const tenantQuotasPage = PageBlueprint.make({
  name: 'tenant-quotas',
  params: {
    path: '/policies/tenants/:tenant',
    title: 'Tenant quotas',
    icon: createElement(AssessmentIcon),
    loader: () =>
      import('./components/quotas/TenantQuotasPage').then(m =>
        createElement(m.TenantQuotasPage),
      ),
  },
});

const policyAdministrationPage = PageBlueprint.make({
  name: 'policy-administration',
  params: {
    path: '/policies/admin',
    title: 'Policy administration',
    icon: createElement(AssessmentIcon),
    loader: () =>
      import('./components/PolicyAdministrationPage').then(m =>
        createElement(m.PolicyAdministrationPage),
      ),
  },
});

const applicationGroupStudioTab = EntityContentBlueprint.make({
  name: 'application-group-policy-studio',
  params: {
    path: '/policy-studio',
    title: 'Policy Studio',
    filter: isApplicationGroupV1Entity,
    loader: () =>
      import('./studio/PolicyStudioPage').then(m =>
        createElement(m.PolicyStudioPage),
      ),
  },
});

export const policyPlugin = createFrontendPlugin({
  pluginId: 'policy',
  extensions: [
    policyApi,
    studioApi,
    applicationGroupPoliciesTab,
    applicationGroupQuotasTab,
    applicationGroupStudioTab,
    stackPolicyEvaluationsTab,
    policyCatalogDetailsTab,
    tenantQuotasPage,
    policyAdministrationPage,
  ],
});
